import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { CreditCard, ExternalLink } from "lucide-react";

const postRedirect = (path) => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = path;

  const token = document.querySelector('meta[name="csrf-token"]')?.content;
  if (token) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = "authenticity_token";
    input.value = token;
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
};

const formatDate = (value) => {
  if (!value) return null;
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(new Date(value));
};

export default function Billing() {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBilling = async () => {
      const response = await fetch("/api/billing");
      const data = await response.json();
      setBilling(data.billing);
      setLoading(false);
    };

    fetchBilling().catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Container maxW="4xl" py={10}>
        <HStack>
          <Spinner size="sm" />
          <Text>Loading billing...</Text>
        </HStack>
      </Container>
    );
  }

  const trialEndsAt = formatDate(billing?.trial_ends_at);
  const periodEndsAt = formatDate(billing?.current_period_end);
  const canPortal = billing?.portal_available;
  const trialDaysRemaining = billing?.trial_days_remaining;
  const isTrialing = billing?.status === "trialing";
  const isBillingExempt = billing?.billing_exempt;

  return (
    <Container maxW="4xl" py={{ base: 6, md: 10 }} px={{ base: 4, md: 6 }}>
      <Stack spacing={6}>
        <Box>
          <Heading size="lg">Billing</Heading>
          <Text color="gray.600" mt={2}>
            Same Day Openings is $10 per active staff user per month.
          </Text>
        </Box>

        <Box bg="white" border="1px" borderColor="gray.200" borderRadius="md" p={{ base: 4, md: 6 }}>
          <Stack spacing={4}>
            <HStack justify="space-between" align="start" spacing={3}>
              <Box>
                <Text fontSize="sm" color="gray.500">Subscription status</Text>
                <HStack mt={1} flexWrap="wrap">
                  <Badge colorScheme={billing?.access_allowed ? "green" : "orange"}>
                    {isBillingExempt ? "Complimentary" : isTrialing ? "Trial active" : billing?.status || "unknown"}
                  </Badge>
                  <Text fontWeight="semibold">
                    {billing?.active_user_quantity || 0} active staff
                  </Text>
                </HStack>
              </Box>
              <CreditCard size={24} />
            </HStack>

            {!isBillingExempt && isTrialing && trialEndsAt && (
              <Text color="gray.700">
                Your 30-day trial ends {trialEndsAt}
                {Number.isInteger(trialDaysRemaining) ? ` (${trialDaysRemaining} ${trialDaysRemaining === 1 ? "day" : "days"} left).` : "."}
              </Text>
            )}
            {periodEndsAt && billing?.status === "active" && (
              <Text color="gray.700">Current period ends {periodEndsAt}.</Text>
            )}

            {!billing?.access_allowed && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                {billing?.no_active_staff
                  ? "No active staff were found after sync. The trial will start automatically once active staff are available."
                  : "Billing is required to use core scheduling and waitlist workflows."}
              </Alert>
            )}

            <HStack sx={{ "& > button": { width: { base: "100%", sm: "auto" }, minHeight: "44px" } }}>
              {canPortal && (
                <Button
                  variant="outline"
                  leftIcon={<ExternalLink size={16} />}
                  onClick={() => postRedirect("/billing/portal")}
                >
                  Manage billing
                </Button>
              )}
            </HStack>
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
}
